import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { QuestionInputField, QuestionInputObjectValue, QuestionInputValue } from '/index';
import type { EmissionTypeOption } from '/business/shared/state/reducers/emissionTypes';

import {
  ERR_TYPE_NUM_LARGE,
  ERR_TYPE_NUM_SMALL,
  ERR_TYPE_REQUIRED,
  ERR_TYPE_STR_LONG,
  ERR_TYPE_STR_SHORT,
  FIELD_TYPE_NUMBER,
  FIELD_TYPE_SEARCHABLE_DROPDOWN,
  FIELD_TYPE_TXTAREA,
} from '/business/shared/components/questions/helper/fieldEnums';

type UseGeneratedWasteOpsSchemaProps = {
  hasAnswer?: boolean;
  wasteTypes: EmissionTypeOption[];
};

type TreatmentType = {
  displayName: string;
  name: string;
};

function getStringInputValue(value: QuestionInputValue | QuestionInputObjectValue | undefined): string | null {
  return typeof value === 'string' ? value : null;
}

export default function useGeneratedWasteOpsSchema({ wasteTypes: _wasteTypesRaw }: UseGeneratedWasteOpsSchemaProps): {
  pageSchema: QuestionInputField[];
} {
  const { t } = useTranslation();
  const [valueWasteType, setValueWasteType] = useState<string | null>(null);

  const validationValue = 999999;
  const wasteTypes = useMemo(
    () => [...new Map(_wasteTypesRaw.map((item) => [item.displayName, item])).values()],
    [_wasteTypesRaw],
  );

  const treatmentTypes = useMemo(() => {
    const treatmentTypesFull: TreatmentType[] = [
      {
        name: 'recycling',
        displayName: 'Recycling',
      },
      {
        name: 'combustion',
        displayName: 'Combustion',
      },
      {
        name: 'composting',
        displayName: 'Composting',
      },
      {
        name: 'landfill',
        displayName: 'Landfill',
      },
    ];
    if (valueWasteType !== null && valueWasteType !== '') {
      const wasteTypeGroups = valueWasteType.split('_');
      wasteTypeGroups.pop();
      const wasteTypeGroup = wasteTypeGroups.join('_');
      return treatmentTypesFull.filter((item) => {
        return _wasteTypesRaw.find((subItem) => {
          return subItem.name === `${wasteTypeGroup}_${item.name}`;
        });
      });
    }
    return [];
  }, [valueWasteType, _wasteTypesRaw]);

  const wasteType: QuestionInputField = useMemo(() => {
    return {
      type: FIELD_TYPE_SEARCHABLE_DROPDOWN,
      element: 'waste_type',
      propName: 'wasteType',
      label: 'Emission source:',
      placeholderI18nKey: 'Select type of waste',
      dropdownTitleI18nKey: 'Select type of waste',
      saveMeCallback: () => {
        return {
          wasteType: _wasteTypesRaw.find(({ name }) => name === valueWasteType),
          hasWasteType: true,
        };
      },
      values: wasteTypes,
      styleInputParentWidth: '20rem',
      onChangeCallback: (value) => {
        setValueWasteType(getStringInputValue(value));
      },

      validation: [
        {
          rule: ERR_TYPE_REQUIRED,
          errCode: 'ERRNOFUELTYPE',
          errStr: t('business:Errors.waste.provideType'),
        },
      ],
    };
  }, [setValueWasteType, wasteTypes, _wasteTypesRaw, valueWasteType, t]);

  const treatmentType: QuestionInputField = useMemo(() => {
    return {
      type: FIELD_TYPE_SEARCHABLE_DROPDOWN,
      hideDropdownError: true,
      element: 'treatment_type',
      propName: 'treatmentType',
      label: 'How was this waste treated?',
      placeholderI18nKey: 'Select a type of treatment',
      dropdownTitleI18nKey: 'Select a type of treatment',
      isDisabled: !valueWasteType,
      saveMe: !!valueWasteType,
      values: treatmentTypes,
      styleInputParentWidth: '20rem',
      onChangeCallback: (value) => {
        const treatmentValue = getStringInputValue(value);
        if (treatmentValue && valueWasteType) {
          const splitWasteType = valueWasteType.split('_');
          const nameStage1 = splitWasteType.slice(0, splitWasteType.length - 1);
          const nameStage2 = [...nameStage1, treatmentValue];
          const nameStage3 = nameStage2.join('_');
          setValueWasteType(nameStage3);
        }
      },
      validation: [
        {
          rule: ERR_TYPE_REQUIRED,
          errCode: 'ERRNOFUELTYPE',
          errStr: t('business:Errors.waste.provideTreatmentType'),
        },
      ],
    };
  }, [valueWasteType, treatmentTypes, t]);

  // const postHeader = useMemo(() => {
  //   return (
  //       <div key="subtitlewaste-estimate">
  //         {valueHasWasteType && (
  //             <StyledHeading key="subtitle-estimate-inner" $midGrey as="div" $type="h3">
  //
  //               Select the types of waste generated by your business during the reporting period: <ReportingPeriod $green /> and how that waste
  //               was disposed of and treated.
  //             </StyledHeading>
  //         )}
  //       </div>
  //   );
  // }, [valueHasWasteType]);

  const quantity: QuestionInputField = useMemo(() => {
    const wasteUnits = _wasteTypesRaw.map((item) => {
      const typeUnitMapped = item?.units?.map((typeUnit) => {
        if (typeof typeUnit !== 'string') {
          return typeUnit;
        }
        const transLitDisplay = t('business:emissionFactors.volumes.'.concat(typeUnit), typeUnit);
        return {
          name: typeUnit,
          displayName: transLitDisplay,
        };
      });
      return {
        ...item,
        units: typeUnitMapped,
      };
    });
    return {
      element: 'quantity',
      propName: 'quantity',
      type: FIELD_TYPE_NUMBER,
      saveMe: !!valueWasteType,
      styleInputParentWidth: '20rem',
      dropdown: {
        element: 'waste_unit',
        values: wasteUnits?.find(({ name }) => name === valueWasteType)?.units,
        propName: 'wasteUnit',
      },
      label: 'Quantity:',

      validation: [
        {
          rule: ERR_TYPE_REQUIRED,
          errCode: 'ERRNOQUANTITY',
          errStr: t('business:Errors.waste.validQuantity'),
        },
        {
          rule: ERR_TYPE_NUM_SMALL,
          errCode: 'ERRQUANTITYTOOLOW',
          errStr: t('business:Errors.waste.validQuantity'),
          value: 1,
        },
        {
          rule: ERR_TYPE_NUM_LARGE,
          errCode: 'ERRQUANTITYTOOHIGH',
          errStr: t('business:Errors.waste.validQuantity'),
          value: validationValue,
        },
      ],
    };
  }, [validationValue, _wasteTypesRaw, valueWasteType, t]);

  const dataSource: QuestionInputField = useMemo(() => {
    return {
      type: FIELD_TYPE_TXTAREA,
      propName: 'dataSource',
      saveMe: !!valueWasteType,
      element: 'data_source',
      labelI18nKey: 'business:QuestionPage.forms.refrigerants.para6',
      descriptionI18nKey: 'business:QuestionPage.forms.refrigerants.para7',
      placeholderI18nKey: 'e.g. Supplier waste management portals',
      validation: [
        {
          rule: ERR_TYPE_REQUIRED,
          errCode: 'ERRDATASOURCE',
          errStr: t('business:Errors.dataSource.provide'),
        },
        {
          rule: ERR_TYPE_STR_SHORT,
          errCode: 'ERRDATASOURCETOOSHORT',
          errStr: t('business:Errors.dataSource.provide'),
          value: 5,
        },
        {
          rule: ERR_TYPE_STR_LONG,
          errCode: 'ERRDATASOURCETOOLONG',
          errStr: t('business:Errors.dataSource.provide'),
          value: 3000,
        },
      ],
    };
  }, [valueWasteType, t]);

  const pageSchema: QuestionInputField[] = useMemo(
    () => [wasteType, treatmentType, quantity, dataSource],
    [wasteType, treatmentType, quantity, dataSource],
  );
  return {
    pageSchema,
  };
}
